import { useDataAssuranceDashboard } from "../../api/dashboard";
import ContentContainer from "../ContentContainer";
import EvidenceSection from "./EvidenceSection";
import EmissionFactorsSection from "./EmissionFactorsSection";
import { useEffect } from "react";
import ReportingScopeSection from "./ReportingScopeSection";
import { usePaginatedEmissionsSources } from "../../api/emissions-sources";
import SectionTitle from "../SectionTitle";
import GridTable from "./GridTable";
import { formatNumber } from "../../utils/formatNumber";
import { groupBy } from "../../utils/groupBy";

const dataStatusIdToPCAFScore = {
    1: 1,
    2: 2,
    3: 2,
    4: 3,
    5: 4,
    6: 5,
    7: 5,
};

const AssuranceDashboard = () => {
    const { isLoading, assuranceDashboardData } = useDataAssuranceDashboard();
    const { emissionsSources } =
        usePaginatedEmissionsSources();

    // Remember scroll y
    useEffect(() => {
        const saveScrollPosition = () => {
            sessionStorage.setItem(
                "assurance-dashboard-scroll-y",
                window.scrollY.toString()
            );
        };

        window.addEventListener("mousedown", saveScrollPosition);

        const scrollY = sessionStorage.getItem("assurance-dashboard-scroll-y");

        if (scrollY !== null) {
            setTimeout(() => {
                window.scrollTo(window.scrollX, parseFloat(scrollY));
            }, 500);

            sessionStorage.removeItem("assurance-dashboard-scroll-y");
        }

        return () => {
            window.removeEventListener("mousedown", saveScrollPosition);
        };
    }, []);

    if (isLoading || !assuranceDashboardData) {
        return (
            <ContentContainer className="py-10 text-center">
                Loading...
            </ContentContainer>
        );
    }

    const averagePCAFScoreBySector = Object.entries(
        groupBy(emissionsSources, (es) => es.sectorName)
    ).map(([sectorName, assets]) => {

        const totalOutstandingAmount = assets.reduce(
            (acc, a) => acc + a.outstandingAmount,
            0
        );

        return {
            sectorName,
            id: assets[0].sectorId,
            count: assets.length,
            outstandingAmount: totalOutstandingAmount,
            score:
                formatNumber(assets.reduce(
                    (acc, a) => acc + (a.outstandingAmount / totalOutstandingAmount) * dataStatusIdToPCAFScore[a.dataStatusId],
                    0
                ) / assets.length, 1),
        };
    });

    const totalEmissionsSourcesCount =
        assuranceDashboardData.reportedVerifiedEmissionsSourcesCount +
        assuranceDashboardData.reportedUnverifiedEmissionsSourcesCount +
        assuranceDashboardData.measuredProductionEmissionsSourcesCount +
        assuranceDashboardData.measuredElectricityEmissionsSourcesCount +
        assuranceDashboardData.estimatedFromSectorATREmissionsSourcesCount +
        assuranceDashboardData.estimatedFromCompanyRevenueEmissionsSourcesCount +
        assuranceDashboardData.estimatedFromAssetValueCountEmissionsSourcesCount;

    if (totalEmissionsSourcesCount === 0) {
        return (
            <ContentContainer className="space-y-8 mb-6">
                <p className="text-center">
                    You have no data yet. Please add your first emissions source
                    in order to see data here.
                </p>
            </ContentContainer>
        );
    }

    return (
        <ContentContainer className="space-y-8 mb-6">
            <ReportingScopeSection
                notMaterialEmissionsSourcesCount={
                    assuranceDashboardData.notMaterialEmissionsSourcesCount
                }
                reportedVerifiedEmissionsSourcesCount={
                    assuranceDashboardData.reportedVerifiedEmissionsSourcesCount
                }
                reportedUnverifiedEmissionsSourcesCount={
                    assuranceDashboardData.reportedUnverifiedEmissionsSourcesCount
                }
                measuredElectricityEmissionsSourcesCount={
                    assuranceDashboardData.measuredElectricityEmissionsSourcesCount
                }
                measuredProductionEmissionsSourcesCount={
                    assuranceDashboardData.measuredProductionEmissionsSourcesCount
                }
                estimatedFromAssetValueCountEmissionsSourcesCount={
                    assuranceDashboardData.estimatedFromAssetValueCountEmissionsSourcesCount
                }
                estimatedFromCompanyRevenueEmissionsSourcesCount={
                    assuranceDashboardData.estimatedFromCompanyRevenueEmissionsSourcesCount
                }
                estimatedFromSectorATREmissionsSourcesCount={
                    assuranceDashboardData.estimatedFromSectorATREmissionsSourcesCount
                }
            />
            <SectionTitle title="Sectoral PCAF Scores" />
            <div className="flex lg:space-x-6 items-center">
                <GridTable
                    gridTemplateColumns="1fr 120px 150px 120px"
                    data={averagePCAFScoreBySector}
                    headings={[
                        { label: "Sector" },
                        { label: "Count" },
                        { label: "Outstanding Amount (£)" },
                        { label: "Score" },
                    ]}
                    renderRow={(rowData) => (
                        <>
                            <div className="px-4 py-3 whitespace-nowrap overflow-hidden text-ellipsis">
                                {rowData.sectorName}
                            </div>
                            <div className="px-4 py-3 whitespace-nowrap overflow-hidden text-ellipsis">
                                {rowData.count}
                            </div>
                            <div className="px-4 py-3 whitespace-nowrap overflow-hidden text-ellipsis">
                                {formatNumber(rowData.outstandingAmount)}
                            </div>
                            <div className="px-4 py-3 whitespace-nowrap overflow-hidden text-ellipsis">
                                {rowData.score}
                            </div>
                        </>
                    )}
                />
            </div>
            <EvidenceSection
                evidenceMissingCount={
                    assuranceDashboardData.organizationEvidenceMissingCount
                }
                evidenceExpiringInNext30DaysCount={
                    assuranceDashboardData.organizationEvidenceExpiringInNext30DaysCount
                }
                evidenceInDateCount={
                    assuranceDashboardData.organizationEvidenceInDateCount
                }
                dataSourceEvidenceByRegion={
                    assuranceDashboardData.emissionsSourceEvidenceByRegion
                }
                evidenceBySector={
                    assuranceDashboardData.emissionsSourceEvidenceBySector
                }
            />
            <EmissionFactorsSection
                activityDataPointsTotalCount={
                    assuranceDashboardData.activityDataPointsTotalCount
                }
                dataSourcesTotalCount={
                    assuranceDashboardData.dataSourcesTotalCount
                }
                activityDataPointsWithNoEmissionsFactorCount={
                    assuranceDashboardData.activityDataPointsWithNoEmissionsFactorCount
                }
                dataSourcesWithNoEmissionsFactorCount={
                    assuranceDashboardData.dataSourcesWithNoEmissionsFactorCount
                }
            />
        </ContentContainer>
    );
};

export default AssuranceDashboard;
